/**
 * **当前账本的本币**（`ledgers.baseCurrency`）—— 端侧取"本币"的唯一入口。
 *
 * ## 为什么需要一个 hook 而不是各页自己找
 *
 * 有些金额**表里根本没有币种类**，按 `DR-004` 它们隐含**本币**（如 `insurance_policies`
 * 的保额 / 现金价值）。若每个页面各自拼一遍"取账本 → 找当前那个 → 读 baseCurrency"，
 * 那就是同一事实的第 N 份实现（铁律 24）—— 而且每一份都可能选错（例如拿列表第一项当"当前"）。
 *
 * ## 数据来源（两个都是现成的，**零新增接口**）
 *
 * | 来源 | 角色 |
 * |---|---|
 * | `useApp().session.ledgerId` | **当前账本是谁** —— 架构 §6.2：账本上下文的唯一来源是 `X-Ledger-Id`（不得从 JWT 推导） |
 * | `GET /api/ledgers`（`['ledgers']`） | 该账本的 `baseCurrency` —— ⚠️ 外壳（`Shell.tsx` 的账本切换器）**已经在查**，故这里是**读缓存**、不产生额外请求 |
 *
 * ## 返回 `null` 的含义
 *
 * `null` = **还不知道本币**（账本列表未回来 / 当前账本不在列表里）。
 * 🔴 调用方**不得**用 `'CNY'` 之类兜底 —— 那会把"不知道"伪装成"人民币"（`DR-036` D5 同源纪律）。
 * 正解：此时不该渲染任何金额（显示加载态或跳过该块）。
 */

import { useCurrentLedger } from './useCurrentLedger.js';

/**
 * 🔴 实现已收拢到 `useCurrentLedger()`（㊼ 批抽出）—— 本函数只是它的一个**字段投影**。
 *
 * 为什么不留一份自己的 `ledgers.data?.find(...)`：流水页还要用同一份账本的 `timezone`
 * ⇒ "谁是当前账本"必须只有一处判据（否则两处都可能选错，且都不报错）。
 */
export function useBaseCurrency(): string | null {
  return useCurrentLedger()?.baseCurrency ?? null;
}
